import { TabContext } from "@mui/lab";
import { Box, Divider, useTheme } from "@mui/material";
import { t } from "i18next";
import { Common } from "../../../../common/src";
import {
  DETAIL_TAB,
  EDisplayType,
  EViewerMode,
  ProjectDetailContext,
  UseSnackbarContext,
} from "../../../pages/ProjectDetail";
import Q3DIconButton from "../../Parts/Q3DIconButton";
import PhotoCamera from "../../../../images/icons/white_edit/PhotoCamera_white.svg";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";

import { DefaultTabPanel } from "./DefaultTabPanel";
import { CalcTabPanel } from "./CalcTabPanel";
import { HelmTabPanel } from "./HelmTabPanel";
import { RemoTabPanel } from "./RemoTabPanel";
import { ExpoTabPanel } from "./ExpoTabPanel";
import { useContext } from "react";
import { setViewType } from "../../../viewer/config/ViewerConfig";
import html2canvas from "html2canvas";
import { ShowToolMenu } from "./ShowToolMenu";
import {
  BoxBackIcon,
  BoxBottomIcon,
  BoxFrontIcon,
  BoxLeftIcon,
  BoxTopSameDirectionIcon,
  BoxRightIcon,
  BoxTopIcon,
  PhotoIcon,
} from "../../Parts/Q3DIcons";

const DARK = "dark";

type Props = {
  projectDetail: Common.ProjectDetail;
  currentViewerMode: EViewerMode;
};

export const ToolBar = (props: Props) => {
  const theme = useTheme();
  const { viewer, displayType, detailTab } = useContext(ProjectDetailContext);
  const { openSuccess, openError } = useContext(UseSnackbarContext);

  const customBackgroundColor = {
    background: theme.palette.base.main,
  };
  const customColor = {
    color: theme.palette.base.contrast,
  };

  const CustomPhotoCamera = () => {
    return (
      // <div style={disabledClipboard() ? { ...customColor, opacity: 0.5 } : null} >
      <div>
        <img src={PhotoCamera} alt="" />
      </div>
    );
  };

  const btnSize = "36px";
  const toolBarLeftWidth = "calc(100% - 360px - 100ppx)"; // btnSize * 10

  const takeScreenshot = async () => {
    const photoShot: HTMLElement = document.querySelector("#point-view3");
    html2canvas(photoShot)
      .then((canvas) => {
        if (displayType === EDisplayType.Web) {
          canvas.toBlob((blob) =>
            navigator.clipboard.write([
              new ClipboardItem({ "image/png": blob }),
            ])
          );
        } else {
          const blob = canvas.toDataURL("image/jpeg");
          (window as any).webkit.messageHandlers.callback.postMessage({
            type: "clipboard",
            props: { image: blob },
          });
        }
        openSuccess(t("COPY_SUCCESS"));
      })
      .catch(() => {
        openError(t("COPY_FAILED"));
      });
  };

  // const disabledProjectState = () => {
  //   return whilePointCloudLoading || currentViewerMode !== EViewerMode.DEFAULT;
  // }

  return (
    <Box
      sx={{
        width: "100%",
        padding: "4px 16px",
        display: "flex",
        justifyContent: "space-between",
        ...customBackgroundColor,
      }}
      // container
      alignItems="center"
    >
      {/* <Box item xs={4}> */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          wordWrap: "break-word",
          minWidth: "0",
          marginRight:
            props.projectDetail?.status.ply_upload !==
            Common.PROJECT_STATUS_ENUM.COMPLETED
              ? "0px"
              : "48px",
        }}
      >
        <Q3DIconButton
          title={t("SCREENSHOT")}
          titleType="tooltip"
          onClick={() => {
            takeScreenshot();
          }}
          icon={<PhotoIcon />}
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Divider
          orientation="vertical"
          sx={{ border: "4px solid #00000000" }}
        />
        <Q3DIconButton
          title={t("CENTER")}
          titleType="tooltip"
          onClick={() => {
            viewer.fit();
          }}
          icon={
            <GpsFixedIcon
              fontSize="small"
              style={{ width: "24px", height: "24px", ...customColor }}
            />
          }
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Q3DIconButton
          title={t("FRONT")}
          titleType="tooltip"
          onClick={() => {
            viewer.setView(setViewType.FRONT);
          }}
          icon={<BoxFrontIcon width={24} height={24} />}
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Q3DIconButton
          title={t("BACK")}
          titleType="tooltip"
          onClick={() => {
            viewer.setView(setViewType.BACK);
          }}
          icon={<BoxBackIcon width={24} height={24} />}
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Q3DIconButton
          title={t("LEFT")}
          titleType="tooltip"
          onClick={() => {
            viewer.setView(setViewType.LEFT);
          }}
          icon={<BoxLeftIcon width={24} height={24} />}
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Q3DIconButton
          title={t("RIGHT_SIDE")}
          titleType="tooltip"
          onClick={() => {
            viewer.setView(setViewType.RIGHT);
          }}
          icon={<BoxRightIcon width={24} height={24} />}
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Q3DIconButton
          title={t("ABOVE")}
          titleType="tooltip"
          onClick={() => {
            viewer.setView(setViewType.TOP);
          }}
          icon={<BoxTopIcon width={24} height={24} />}
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Q3DIconButton
          title={t("BOTTOM")}
          titleType="tooltip"
          onClick={() => {
            viewer.setView(setViewType.BOTTOM);
          }}
          icon={<BoxBottomIcon width={24} height={24} />}
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Q3DIconButton
          title={t("TOP_SAME_DIRECTION")}
          titleType="tooltip"
          onClick={() => {
            viewer.setView(setViewType.TOP_SAME_DIRECTION);
          }}
          icon={<BoxTopSameDirectionIcon width={24} height={24} />}
          height={btnSize}
          width={btnSize}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <Divider
          orientation="vertical"
          sx={{ border: "4px solid #00000000" }}
        />
        <ShowToolMenu projectDetail={props.projectDetail} />
      </Box>
      {/* </Grid>
      <Grid item xs={8} sx={{}}> */}
      <TabContext value={props.currentViewerMode}>
        {displayType === EDisplayType.Web ? (
          <DefaultTabPanel width={`${toolBarLeftWidth}`} />
        ) : (
          <></>
        )}
        <CalcTabPanel width={`${toolBarLeftWidth}`} />
        <HelmTabPanel width={`${toolBarLeftWidth}`} />
        <RemoTabPanel width={`${toolBarLeftWidth}`} />
        <ExpoTabPanel width={`${toolBarLeftWidth}`} />
      </TabContext>
      {/* </Grid> */}
    </Box>
  );
};